import React, { useEffect } from 'react';

interface ConnectAccountModalProps {
  open: boolean;
  onClose: () => void;
  platform: {
    id: string;
    name: string;
    icon: string;
  } | null;
}

const PLATFORM_INFO: Record<string, { provider: string; description: string; features: string[] }> = {
  youtube: {
    provider: 'youtube',
    description: 'Connect your YouTube channel to publish Shorts',
    features: ['Upload YouTube Shorts', 'Auto-optimize for mobile viewing', 'Track engagement metrics'],
  },
  instagram: {
    provider: 'instagram',
    description: 'Connect your Instagram Business account to publish Reels',
    features: ['Post Instagram Reels', 'Schedule content', 'Track performance'],
  },
  tiktok: {
    provider: 'tiktok',
    description: 'Connect your TikTok account to publish videos',
    features: ['Upload TikTok videos', 'Manage duet & stitch settings', 'View analytics'],
  },
  facebook: {
    provider: 'facebook',
    description: 'Connect your Facebook Page to publish Reels',
    features: ['Post Facebook Reels', 'Cross-post to Instagram', 'Engagement tracking'],
  },
  x: {
    provider: 'twitter',
    description: 'Connect your X (Twitter) account to share posts',
    features: ['Post tweets with media', 'Schedule posts', 'Track engagement'],
  },
  linkedin: {
    provider: 'linkedin',
    description: 'Connect your LinkedIn profile to share content',
    features: ['Publish professional videos', 'Reach your network', 'Track views & engagement'],
  },
};

const ConnectAccountModal: React.FC<ConnectAccountModalProps> = ({ open, onClose, platform }) => {
  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open || !platform) return null;

  const info = PLATFORM_INFO[platform.id];
  if (!info) return null;

  const handleConnect = () => {
    // Redirect to OAuth flow
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    window.location.href = `${API_BASE}/auth/${info.provider}`;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container connect-account-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <span className="connect-modal-icon">{platform.icon}</span>
            Connect {platform.name}
          </h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="connect-modal-body">
          <p className="connect-modal-description">{info.description}</p>

          <div className="connect-modal-features">
            <h4>What you can do:</h4>
            <ul>
              {info.features.map((feature, idx) => (
                <li key={idx}>
                  <span className="feature-check">✓</span>
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          <div className="connect-modal-note">
            <span className="note-icon">🔒</span>
            <p>We'll redirect you to {platform.name} to authorize access. Your credentials are never shared with us.</p>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={handleConnect}>
            Connect with {platform.name}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConnectAccountModal;
